import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomeCadastro, nomeParceiro, numero } from '../../nucleo/formato';
import { useSessaoFluxo } from './AvisoModo';
import { invalidarCompras, qtd, type Comparativo as DadosComparativo } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/**
 * Quadro comparativo das cotações de uma requisição (M10.1): preço e prazo por produto e fornecedor, com o menor
 * preço de cada linha e o menor total marcados. Em requisição aprovada, quem pode aprovar escolhe a vencedora aqui.
 */
export function Comparativo({ requisicaoId, aoFechar }: { requisicaoId: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/compras/requisicoes', 'comparativo', requisicaoId], queryFn: () => get<DadosComparativo>(`/compras/requisicoes/${requisicaoId}/comparativo`), enabled: !!requisicaoId });
  const escolher = useMutation({
    mutationFn: (id: string) => post(`/compras/cotacoes/${id}/escolher`),
    onSuccess: async () => {
      await invalidarCompras(qc);
      toast.sucesso(t('mensagens.cotacaoEscolhida'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const moeda = s.workspace.moeda;
  const d = q.data;
  const podeEscolher = !!d && d.requisicao.estado === 'aprovada' && s.pode('compras.aprovar');

  return (
    <Modal aberto={!!requisicaoId} aoFechar={aoFechar} largura="lg" titulo={d ? t('comparativo.titulo', { numero: d.requisicao.numero }) : t('comparativo.tituloCurto')} rodape={<Botao onClick={aoFechar}>{tn('comum.fechar')}</Botao>}>
      {q.isPending && !!requisicaoId && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {d && d.cotacoes.length === 0 && <Vazio texto={t('comparativo.vazio')} />}
      {d && d.cotacoes.length > 0 && (
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-sm text-tinta-3">{t('comparativo.legenda')}</p>
          <div className="overflow-x-auto rounded-lg border border-borda">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borda text-left">
                  <th scope="col" className="px-3 py-2 font-medium text-tinta-3">
                    {t('campos.produto')}
                  </th>
                  {d.cotacoes.map((c) => (
                    <th key={c.id} scope="col" className="min-w-36 px-3 py-2 text-right font-medium text-tinta-1">
                      <span className="flex flex-col items-end gap-1">
                        <span className="max-w-40 truncate" title={nomeParceiro(c.fornecedor_nome)}>{nomeParceiro(c.fornecedor_nome)}</span>
                        {c.escolhida && <Selo cor="verde">{t('cotacao.escolhida')}</Selo>}
                        {c.vencida && <Selo cor="laranja">{t('cotacao.vencida')}</Selo>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.produtos.map((p) => (
                  <tr key={p.produto_id} className="border-b border-borda">
                    <td className="px-3 py-2">
                      <span className="block max-w-56 truncate" title={p.produto_nome ?? undefined}>{nomeCadastro(p.produto_nome)}</span>
                      <span className="numero block text-xs text-tinta-3">
                        {p.produto_codigo} · {qtd(p.quantidade, numero)} {p.unidade ?? ''}
                      </span>
                    </td>
                    {d.cotacoes.map((c) => {
                      const o = p.ofertas.find((x) => x.cotacao_id === c.id);
                      return (
                        <td key={c.id} className="numero px-3 py-2 text-right">
                          {o ? (
                            <span className="flex flex-col items-end">
                              <span className={o.menor_preco ? 'font-semibold text-verde' : undefined}>{anunciarOcultos(dinheiro(o.preco_unitario_centavos, moeda))}</span>
                              <span className="text-xs text-tinta-3">{o.prazo_dias === null ? '' : t('comparativo.prazoDias', { count: o.prazo_dias })}</span>
                            </span>
                          ) : (
                            <span className="text-tinta-3">{t('comparativo.semOferta')}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-b border-borda bg-sup-2">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {t('campos.total')}
                  </th>
                  {d.cotacoes.map((c) => (
                    <td key={c.id} className={`numero px-3 py-2 text-right font-semibold ${c.menor_total ? 'text-verde' : ''}`}>
                      {anunciarOcultos(dinheiro(c.total_centavos, moeda))}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-borda">
                  <th scope="row" className="px-3 py-2 text-left font-medium text-tinta-3">
                    {t('campos.prazoMaximo')}
                  </th>
                  {d.cotacoes.map((c) => (
                    <td key={c.id} className="numero px-3 py-2 text-right">
                      {c.prazo_maximo_dias === null ? '' : t('comparativo.prazoDias', { count: c.prazo_maximo_dias })}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-borda">
                  <th scope="row" className="px-3 py-2 text-left font-medium text-tinta-3">
                    {t('campos.condicao')}
                  </th>
                  {d.cotacoes.map((c) => (
                    <td key={c.id} className="numero px-3 py-2 text-right">
                      {c.condicao_pagamento === 'avista' ? t('pedido.avista') : (c.condicao_pagamento ?? '')}
                    </td>
                  ))}
                </tr>
                <tr className={podeEscolher ? 'border-b border-borda' : ''}>
                  <th scope="row" className="px-3 py-2 text-left font-medium text-tinta-3">
                    {t('campos.validade')}
                  </th>
                  {d.cotacoes.map((c) => (
                    <td key={c.id} className={`numero px-3 py-2 text-right ${c.vencida ? 'text-laranja' : ''}`}>
                      {c.validade ? data(c.validade) : ''}
                    </td>
                  ))}
                </tr>
                {podeEscolher && (
                  <tr>
                    <td className="px-3 py-2" />
                    {d.cotacoes.map((c) => (
                      <td key={c.id} className="px-3 py-2 text-right">
                        <Botao tamanho="sm" variante={c.escolhida ? 'secundario' : 'primario'} disabled={c.escolhida || c.vencida} carregando={escolher.isPending && escolher.variables === c.id} icone={<Check className="size-4" />} onClick={() => escolher.mutate(c.id)}>
                          {c.escolhida ? t('cotacao.escolhida') : t('acoes.escolherCotacao')}
                        </Botao>
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
